import React, { forwardRef, useImperativeHandle } from 'react';
import { FormProvider } from 'react-hook-form';
import * as yup from 'yup';
import { ExtraFieldInternalValue } from '../extraFieldInternalValue';
import { useExtraFieldsFormValidation } from '../useExtraFieldsFormValidation';
import {
  ExtraFieldsAreaContent,
  Props as ExtraFieldsAreaContentProps,
} from './ExtraFieldsAreaContent';
import { useExtraFieldsFormSimple } from './useExtraFieldsFormSimple';

type Props = ExtraFieldsAreaContentProps & {
  onChange: (
    extraFields: ExtraFieldInternalValue[],
    recordTypeName: string | undefined,
  ) => void;
  values?: readonly ExtraFieldInternalValue[];
  recordTypeName: string | null;
};

export type ExtraFieldsAreaContentSimpleRef = {
  triggerValidation: () => Promise<boolean>;
};

export const ExtraFieldsAreaContentSimple = forwardRef<
  ExtraFieldsAreaContentSimpleRef,
  Props
>(({ onChange, values, recordTypeName, ...restProps }, ref) => {
  const { applyExtraFieldsValidationSchema } = useExtraFieldsFormValidation({
    extraFieldFormInfos: restProps.formFields,
    contextParams: restProps.contextParams ?? [],
  });

  const validationSchema = applyExtraFieldsValidationSchema(yup.object());
  const { formMethods } = useExtraFieldsFormSimple({
    values,
    recordTypeName,
    availableRecordTypes: restProps.availableRecordTypes,
    onChange,
    validationSchema,
    inputsMounted: restProps.formFields.length > 0,
  });

  const { trigger } = formMethods;
  useImperativeHandle(ref, () => ({ triggerValidation: trigger }), [trigger]);

  return (
    <FormProvider {...formMethods}>
      <ExtraFieldsAreaContent {...restProps} />
    </FormProvider>
  );
});

ExtraFieldsAreaContentSimple.displayName = 'ExtraFieldsAreaContentSimple';
